import React, { useState } from 'react';
import { ActiveUsers } from '../types';

interface Props {
  data: ActiveUsers;
}

type TimePeriod = '30' | '90' | '365';

export const ActiveUsersCard: React.FC<Props> = ({ data }) => {
  const [period, setPeriod] = useState<TimePeriod>('30');

  const periodValue = {
    '30': data.active_last_30_days,
    '90': data.active_last_90_days,
    '365': data.active,
  };

  const activeCount = periodValue[period];
  const activePercent = data.total > 0 ? ((activeCount / data.total) * 100).toFixed(1) : '0';

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: '#6b7280' }}>Active Users</span>
        <div style={{ display: 'flex', backgroundColor: '#f3f4f6', borderRadius: 6, padding: 2 }}>
          {(['30', '90', '365'] as TimePeriod[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              style={{
                padding: '3px 8px',
                fontSize: 11,
                fontWeight: 500,
                borderRadius: 4,
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.15s',
                backgroundColor: period === p ? '#fff' : 'transparent',
                color: period === p ? '#111827' : '#6B7280',
                boxShadow: period === p ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
              }}
            >
              {p}d
            </button>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 4 }}>
        <span style={{ fontSize: 32, fontWeight: 600, color: '#111827', fontVariantNumeric: 'tabular-nums' }}>
          {activeCount}
        </span>
        <span style={{ fontSize: 14, color: '#6B7280', fontWeight: 500, marginLeft: 4 }}>/ {data.total}</span>
      </div>
      <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 20px' }}>
        active in last {period} days ({activePercent}%)
      </p>

      {/* Mini bar visualization */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {(['30', '90', '365'] as TimePeriod[]).map((p) => {
          const val = periodValue[p];
          const pct = data.total > 0 ? (val / data.total) * 100 : 0;
          const isSelected = p === period;
          return (
            <div key={p} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                fontSize: 11, width: 30, textAlign: 'right' as const,
                fontVariantNumeric: 'tabular-nums',
                fontWeight: isSelected ? 500 : 400,
                color: isSelected ? '#374151' : '#d1d5db',
              }}>
                {p}d
              </span>
              <div style={{ flex: 1, backgroundColor: '#f3f4f6', borderRadius: 99, height: 6 }}>
                <div
                  style={{
                    height: 6, borderRadius: 99,
                    width: `${pct}%`,
                    backgroundColor: isSelected ? '#3b82f6' : '#dbeafe',
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
              <span style={{
                fontSize: 11, width: 28, textAlign: 'right' as const,
                fontVariantNumeric: 'tabular-nums',
                fontWeight: isSelected ? 600 : 400,
                color: isSelected ? '#374151' : '#6B7280',
              }}>
                {val}
              </span>
            </div>
          );
        })}
      </div>

      {/* Status breakdown */}
      <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {data.inactive > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12, color: '#6B7280' }}>Inactive</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', fontVariantNumeric: 'tabular-nums' }}>{data.inactive}</span>
          </div>
        )}
        {data.never_logged_in > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12, color: '#6B7280' }}>Never logged in</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#f59e0b', fontVariantNumeric: 'tabular-nums' }}>{data.never_logged_in}</span>
          </div>
        )}
      </div>
    </div>
  );
};
